import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import axios from 'axios';
import { Button } from '@/components/ui/button';
import { Search, Shield, TrendingUp, Globe, Package, Eye, Clock, MapPin, Zap, Battery, Cpu, ShieldCheck, FileCheck } from 'lucide-react';
import Navbar from '@/components/Navbar';
import Footer from '@/components/Footer';
import { API_URL } from '@/config/api';

export default function LandingPage() {
  const navigate = useNavigate();
  const { t } = useTranslation();
  const [featuredMinerals, setFeaturedMinerals] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => { fetchMarketData(); }, []);

  const fetchMarketData = async () => {
    try {
      const res = await axios.get(`${API_URL}/market/featured-minerals`);
      setFeaturedMinerals(Array.isArray(res.data) ? res.data : []);
    } catch { setFeaturedMinerals([]); }
    finally { setLoading(false); }
  };

  const hotCommodities = [
    { nameKey: 'minerals.coltan', icon: Cpu, demandKey: 'hot.demand.critical', useKey: 'hot.use.electronics', accent: 'from-red-500/20 to-red-900/10 border-red-500/20', badge: 'bg-red-500' },
    { nameKey: 'minerals.copperCathode', icon: Zap, demandKey: 'hot.demand.veryHigh', useKey: 'hot.use.infrastructure', accent: 'from-orange-500/20 to-orange-900/10 border-orange-500/20', badge: 'bg-orange-500' },
    { nameKey: 'minerals.cobalt', icon: Battery, demandKey: 'hot.demand.high', useKey: 'hot.use.evBatteries', accent: 'from-blue-500/20 to-blue-900/10 border-blue-500/20', badge: 'bg-blue-500' },
    { nameKey: 'minerals.lithium', icon: Battery, demandKey: 'hot.demand.critical', useKey: 'hot.use.batteryProduction', accent: 'from-purple-500/20 to-purple-900/10 border-purple-500/20', badge: 'bg-purple-500' },
    { nameKey: 'minerals.manganese', icon: Package, demandKey: 'hot.demand.high', useKey: 'hot.use.steel', accent: 'from-teal-500/20 to-teal-900/10 border-teal-500/20', badge: 'bg-teal-500' },
    { nameKey: 'minerals.copper', icon: Shield, demandKey: 'hot.demand.high', useKey: 'hot.use.smelting', accent: 'from-cyan-500/20 to-cyan-900/10 border-cyan-500/20', badge: 'bg-cyan-500' },
  ];

  return (
    <div className="min-h-screen bg-[#0B1120]">
      <Navbar />
      
      {/* Hero Section */}
      <section className="relative min-h-[600px] flex items-center overflow-hidden">
        <div className="absolute inset-0 bg-[url('https://images.unsplash.com/photo-1689864459067-1f84f2f82342?w=1920&q=80')] bg-cover bg-center" />
        <div className="absolute inset-0 bg-gradient-to-r from-[#0B1120] via-[#0B1120]/90 to-transparent" />
        <div className="relative z-10 max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-24">
          <div className="max-w-2xl">
            <p className="text-xs font-bold uppercase tracking-[0.2em] text-[#F59E0B] mb-4">{t('hero.tagline')}</p>
            <h1 className="text-5xl lg:text-6xl font-black text-white mb-6 leading-none tracking-tighter font-['Outfit']">
              {t('hero.title')}
              <span className="block text-[#F59E0B] mt-2">{t('hero.subtitle')}</span>
            </h1>
            <p className="text-lg text-[#94A3B8] mb-10 leading-relaxed">
              {t('hero.description')}
            </p>
            <div className="flex flex-wrap gap-4">
              <Button
                size="lg"
                onClick={() => navigate('/auth')}
                data-testid="get-started-button"
                className="bg-[#F59E0B] hover:bg-[#D97706] text-[#0B1120] px-8 py-6 text-lg font-bold rounded-md"
              >
                {t('hero.getStarted')}
              </Button>
              <Button
                size="lg"
                variant="outline"
                onClick={() => document.getElementById('hot-commodities')?.scrollIntoView({ behavior: 'smooth' })}
                data-testid="browse-minerals-button"
                className="border-[#1E293B] border-2 text-white hover:bg-white/5 px-8 py-6 text-lg font-semibold rounded-md"
              >
                <Search className="w-5 h-5 mr-2" />
                {t('hero.hotCommodities')}
              </Button>
            </div>
          </div>
        </div>
      </section>

      {/* Hot Commodities */}
      <section id="hot-commodities" className="py-24 bg-[#0B1120]">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <div className="inline-flex items-center px-4 py-2 bg-red-500/10 border border-red-500/20 text-red-400 rounded-full mb-4">
              <Zap className="w-4 h-4 mr-2" />
              <span className="font-bold text-xs uppercase tracking-wider">{t('hot.banner')}</span>
            </div>
            <h2 className="text-3xl sm:text-4xl font-bold text-white mb-4 font-['Outfit']">{t('hot.title')}</h2>
            <p className="text-[#94A3B8] max-w-3xl mx-auto">{t('hot.description')}</p>
          </div>

          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
            {hotCommodities.map((c, idx) => (
              <div
                key={idx}
                className={`bg-gradient-to-br ${c.accent} border rounded-xl p-6 hover:border-[#F59E0B]/40 hover:shadow-[0_8px_30px_rgba(245,158,11,0.1)] transition-all duration-300 cursor-pointer group`}
                onClick={() => navigate('/auth')}
                data-testid={`hot-commodity-${idx}`}
              >
                <div className="flex items-center justify-between mb-4">
                  <div className="w-12 h-12 bg-white/5 rounded-lg flex items-center justify-center group-hover:scale-110 transition-transform">
                    <c.icon className="w-6 h-6 text-[#F59E0B]" />
                  </div>
                  <span className={`${c.badge} text-white text-xs font-bold px-2.5 py-1 rounded-full`}>{t(c.demandKey)}</span>
                </div>
                <h3 className="text-lg font-bold text-white mb-1">{t(c.nameKey)}</h3>
                <p className="text-sm text-[#64748B] mb-4">{t(c.useKey)}</p>
                <div className="px-4 py-2 bg-white/5 text-[#94A3B8] rounded-md text-center text-sm font-medium group-hover:bg-[#F59E0B]/10 group-hover:text-[#F59E0B] transition-colors">
                  {t('hot.viewListings')}
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Featured Minerals */}
      <section id="featured-minerals" className="py-24 bg-[#0D1526]">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <h2 className="text-3xl sm:text-4xl font-bold text-white mb-4 font-['Outfit']">{t('featured.title')}</h2>
            <p className="text-[#94A3B8]">{t('featured.subtitle')}</p>
          </div>

          {loading ? (
            <div className="flex items-center justify-center py-12">
              <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-[#F59E0B]"></div>
            </div>
          ) : featuredMinerals.length === 0 ? (
            <div className="text-center py-12">
              <Package className="w-16 h-16 text-[#1E293B] mx-auto mb-4" />
              <p className="text-xl text-[#94A3B8] mb-6">{t('featured.empty')}</p>
              <Button onClick={() => navigate('/auth')} className="bg-[#F59E0B] hover:bg-[#D97706] text-[#0B1120] font-semibold">{t('featured.startSelling')}</Button>
            </div>
          ) : (
            <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6 mb-8">
              {featuredMinerals.map((mineral) => (
                <div
                  key={mineral.id}
                  className="bg-[#121A2F] border border-[#1E293B] rounded-xl overflow-hidden hover:-translate-y-1 hover:border-[#F59E0B]/30 hover:shadow-[0_8px_30px_rgba(245,158,11,0.12)] transition-all duration-300 cursor-pointer"
                  onClick={() => navigate('/auth')}
                  data-testid={`featured-mineral-${mineral.id}`}
                >
                  <div className="aspect-video bg-[#1A2440] overflow-hidden relative">
                    {mineral.images?.[0] ? (
                      <img src={mineral.images[0]} alt={mineral.title} className="w-full h-full object-cover" />
                    ) : (
                      <div className="w-full h-full flex items-center justify-center">
                        <Package className="w-12 h-12 text-[#1E293B]" />
                      </div>
                    )}
                    {mineral.views > 0 && (
                      <div className="absolute top-2 right-2 bg-black/60 backdrop-blur text-white px-2 py-1 rounded text-xs flex items-center">
                        <Eye className="w-3 h-3 mr-1" /> {mineral.views}
                      </div>
                    )}
                  </div>
                  <div className="p-5">
                    <div className="flex items-center justify-between mb-3">
                      <span className="bg-[#F59E0B]/10 text-[#F59E0B] px-2.5 py-0.5 rounded-full text-xs font-medium border border-[#F59E0B]/20">
                        {mineral.category.replace('_', ' ').toUpperCase()}
                      </span>
                      <span className="text-xl font-bold text-white">
                        ${mineral.price}<span className="text-sm font-normal text-[#64748B]">/{mineral.unit}</span>
                      </span>
                    </div>
                    <h3 className="text-lg font-bold text-white mb-2">{mineral.title}</h3>
                    <p className="text-[#64748B] text-sm mb-3 line-clamp-2">{mineral.description}</p>
                    <div className="flex items-center text-xs text-[#64748B] mb-2">
                      <MapPin className="w-3 h-3 mr-1" /> {mineral.location}
                    </div>
                    <div className="flex items-center justify-between text-xs text-[#64748B]">
                      <span className="flex items-center gap-2">
                        {t('featured.by')} <span className="font-medium text-[#94A3B8]">{mineral.seller_name}</span>
                        {mineral.seller_verified && (
                          <span className="inline-flex items-center gap-0.5 text-[#10B981]" data-testid={`verified-badge-${mineral.id}`}>
                            <ShieldCheck className="w-3.5 h-3.5" /> {t('featured.verified')}
                          </span>
                        )}
                        {mineral.verified_documents_count > 0 && (
                          <span className="inline-flex items-center gap-0.5 text-[#3B82F6]" data-testid={`docs-badge-${mineral.id}`}>
                            <FileCheck className="w-3.5 h-3.5" /> {mineral.verified_documents_count} {mineral.verified_documents_count > 1 ? t('featured.docsPlural') : t('featured.docs')}
                          </span>
                        )}
                      </span>
                      <span className="flex items-center"><Clock className="w-3 h-3 mr-1" />{new Date(mineral.created_at).toLocaleDateString()}</span>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          )}
          
          <div className="text-center">
            <Button onClick={() => navigate('/auth')} size="lg" className="bg-[#1A2440] hover:bg-[#2563EB]/20 text-white border border-[#2563EB]/30 hover:border-[#2563EB] px-8 py-6 text-lg font-semibold rounded-md">
              {t('featured.viewAll')}
            </Button>
          </div>
        </div>
      </section>

      {/* Features */}
      <section className="py-24 bg-[#0B1120]">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <h2 className="text-3xl sm:text-4xl font-bold text-white mb-4 font-['Outfit']">{t('features.title')}</h2>
            <p className="text-[#94A3B8] max-w-2xl mx-auto">{t('features.subtitle')}</p>
          </div>
          <div className="grid md:grid-cols-3 gap-8">
            {[
              { icon: Shield, title: t('features.escrow.title'), desc: t('features.escrow.desc'), color: 'text-[#F59E0B]', bg: 'bg-[#F59E0B]/10' },
              { icon: Globe, title: t('features.buyers.title'), desc: t('features.buyers.desc'), color: 'text-[#2563EB]', bg: 'bg-[#2563EB]/10' },
              { icon: TrendingUp, title: t('features.insights.title'), desc: t('features.insights.desc'), color: 'text-[#10B981]', bg: 'bg-[#10B981]/10' },
            ].map(({ icon: Icon, title, desc, color, bg }, i) => (
              <div key={i} className="bg-[#121A2F] border border-[#1E293B] rounded-xl p-8 hover:border-[#F59E0B]/30 transition-all duration-300 group">
                <div className={`w-14 h-14 ${bg} rounded-lg flex items-center justify-center mb-6 group-hover:scale-110 transition-transform`}>
                  <Icon className={`w-7 h-7 ${color}`} />
                </div>
                <h3 className="text-xl font-bold text-white mb-3">{title}</h3>
                <p className="text-[#94A3B8] leading-relaxed">{desc}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* CTA */}
      <section className="py-24 bg-gradient-to-br from-[#121A2F] to-[#0B1120] border-t border-[#1E293B]">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <h2 className="text-3xl sm:text-4xl font-bold text-white mb-6 font-['Outfit']">{t('cta.title')}</h2>
          <p className="text-lg text-[#94A3B8] mb-10">
            {t('cta.description')}
          </p>
          <Button
            size="lg"
            onClick={() => navigate('/auth')}
            data-testid="join-now-button"
            className="bg-[#F59E0B] hover:bg-[#D97706] text-[#0B1120] px-10 py-6 text-lg font-bold rounded-md"
          >
            {t('cta.button')}
          </Button>
        </div>
      </section>

      <Footer />
    </div>
  );
}
